import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebaseService';
import { tokenCoordinator } from './tokenCoordinationService';

/**
 * Service to proactively monitor token health and refresh before expiration
 * Prevents the cascade of permission-denied errors on long-running sessions
 */
class TokenMonitoringService {
  private monitoringInterval: NodeJS.Timeout | null = null;
  private authStateUnsubscribe: (() => void) | null = null;
  private isMonitoring = false;
  
  // Configuration
  private readonly MONITOR_INTERVAL = 10 * 60 * 1000; // Check every 10 minutes
  private readonly TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh 5 minutes before expiry
  private readonly INITIAL_DELAY = 30 * 1000; // Wait 30 seconds after auth before first check

  /**
   * Start monitoring token health
   */
  startMonitoring() {
    if (this.isMonitoring) {
      console.log('[TokenMonitoring] Already monitoring');
      return;
    }

    console.log('[TokenMonitoring] Starting token monitoring service');
    this.isMonitoring = true;
    
    // Set up periodic monitoring
    this.setupPeriodicMonitoring();
    
    // Monitor auth state changes
    this.setupAuthStateMonitoring();
  }

  /**
   * Stop monitoring token health
   */
  stopMonitoring() {
    if (!this.isMonitoring) return;

    console.log('[TokenMonitoring] Stopping token monitoring service');
    
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }

    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
      this.authStateUnsubscribe = null;
    }

    this.isMonitoring = false;
  }

  /**
   * Set up periodic token health checks
   */
  private setupPeriodicMonitoring() {
    this.monitoringInterval = setInterval(async () => {
      const user = auth.currentUser;
      if (!user) {
        console.log('[TokenMonitoring] No authenticated user, skipping check');
        return;
      }

      try {
        await this.checkAndRefreshTokens(user);
      } catch (error) {
        console.error('[TokenMonitoring] Error during periodic check:', error);
      }
    }, this.MONITOR_INTERVAL);
  }

  /**
   * Set up auth state change monitoring
   */
  private setupAuthStateMonitoring() {
    this.authStateUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('[TokenMonitoring] User authenticated, scheduling initial token check');
        
        // Schedule initial check after a delay to allow auth to stabilize
        setTimeout(async () => {
          try {
            await this.checkAndRefreshTokens(user);
          } catch (error) {
            console.error('[TokenMonitoring] Error during initial auth check:', error);
          }
        }, this.INITIAL_DELAY);
      } else {
        console.log('[TokenMonitoring] User signed out');
      }
    });
  }

  /**
   * Check token expiration and refresh if needed
   */
  private async checkAndRefreshTokens(user: User) {
    try {
      const shouldRefresh = await tokenCoordinator.shouldRefreshTokens(user);
      
      if (shouldRefresh) {
        console.log('[TokenMonitoring] Tokens expiring soon, initiating proactive refresh');
        const refreshSuccess = await tokenCoordinator.coordinatedTokenRefresh(user);
        
        if (refreshSuccess) {
          console.log('[TokenMonitoring] Proactive token refresh successful');
        } else {
          console.warn('[TokenMonitoring] Proactive token refresh failed');
        }
      } else {
        console.log('[TokenMonitoring] Tokens are healthy, no refresh needed');
      }
    } catch (error: any) {
      console.error('[TokenMonitoring] Error checking token health:', error);
      
      // If we can't check token health, attempt a refresh as a safety measure
      if (error.code === 'auth/network-request-failed' || 
          error.code === 'permission-denied' ||
          error.message?.includes('permission')) {
        console.log('[TokenMonitoring] Token health check failed, attempting emergency refresh');
        await tokenCoordinator.coordinatedTokenRefresh(user);
      }
    }
  }

  /**
   * Manually trigger a token health check (useful for debugging)
   */
  async manualTokenCheck(): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) {
      console.log('[TokenMonitoring] No user for manual check');
      return false;
    }

    try {
      await this.checkAndRefreshTokens(user);
      return true;
    } catch (error) {
      console.error('[TokenMonitoring] Manual token check failed:', error);
      return false;
    }
  }

  /**
   * Get monitoring status
   */
  getStatus() {
    return {
      isMonitoring: this.isMonitoring,
      hasPeriodicMonitoring: !!this.monitoringInterval,
      hasAuthStateMonitoring: !!this.authStateUnsubscribe,
      coordinatorStatus: tokenCoordinator.getRefreshStatus()
    };
  }

  /**
   * Force an immediate token refresh (emergency use)
   */
  async emergencyTokenRefresh(): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) {
      console.log('[TokenMonitoring] No user for emergency refresh');
      return false;
    }

    console.log('[TokenMonitoring] Performing emergency token refresh');
    return await tokenCoordinator.coordinatedTokenRefresh(user);
  }
}

// Export singleton instance
export const tokenMonitor = new TokenMonitoringService();
